/**
 * filePathHelper.js
 * Talkroute
 *
 * Created by Vel Genov on 7/14/19
 * Copyright (c) 2019 Talkroute. All right reserved.
 */
import path from "path";

import appConfig from "../config/index";

import apiConstants from "../constants/apiConstants";
import validateArray from "./arrayValidator";

export const localFilePaths = {
  TEMP: "/Temp/",
};

// #region Voicemails

export default class FilePathHelper {
  public userDataPath: string;

  constructor() {
    if (appConfig.electron) {
      this.userDataPath = (
        appConfig.electron.app || appConfig.electron.remote.app
      ).getPath("userData");
    } else {
      this.userDataPath = "/userData";
    }
    if (!appConfig.fileStorage?.exists(this.userDataPath)) {
      appConfig.fileStorage?.createPath(this.userDataPath);
    }
    const tempPath = path.join(this.userDataPath, localFilePaths.TEMP);
    if (!appConfig.fileStorage?.exists(tempPath)) {
      appConfig.fileStorage?.createPath(tempPath);
    }
  }

  /**
   * Get the user file path for Voicemails
   *
   * @returns a string containing the file path
   */
  getVoicemailFilePath = () => {
    const filePath = path.join(
      this.userDataPath,
      apiConstants().voicemailFileSettings.VOICEMAIL_USER_FOLDER
    );

    return filePath;
  };

  /**
   * Get the user file path for Voicemails along with a voicemail filename
   *
   * @returns a string containing the file path
   */
  getVoicemailFilePathAndName = (voicemailId) => {
    const voicemailFilePath = this.getVoicemailFilePath();
    const voicemailFileName = this.getVoicemailFileName(voicemailId);

    const voicemailFilePathAndName = path.join(
      voicemailFilePath,
      voicemailFileName
    );

    return voicemailFilePathAndName;
  };

  /**
   * Get the file name for a voicemail
   *
   * @param voicemailId the ID of the voicemail
   * @returns a file name for a voicemail
   */
  getVoicemailFileName = (voicemailId) => {
    const fileName = `${voicemailId}.${
      apiConstants().voicemailFileSettings.VOICEMAIL_FILE_EXTENSION
    }`;

    return fileName;
  };

  // #endregion Voicemails

  // #region TextMessageAttachments

  /**
   * Get the user file path for TextMessageAttachments
   *
   * @returns a string containing the file path
   */
  getMessageAttachmentBaseFilePath = () => {
    const filePath = path.join(
      this.userDataPath,
      apiConstants().textMessageAttachmentSettings.ATTACHMENT_USER_FOLDER
    );

    if (!appConfig.fileStorage?.exists(filePath)) {
      appConfig.fileStorage?.createPath(filePath);
    }

    return filePath;
  };

  /**
   * Get the user file path for TextMessageAttachments
   *
   * @returns a string containing the file path
   */
  getMessageAttachmentFilePath = (conversationId) => {
    const basePath = this.getMessageAttachmentBaseFilePath();
    const filePath = path.join(basePath, conversationId);

    if (!appConfig.fileStorage?.exists(filePath)) {
      appConfig.fileStorage?.createPath(filePath);

      return "";
    }

    return filePath;
  };

  /**
   * Get the user file path for a TextMessageAttachment
   *
   * @param attachmentId the unique id of the attachment
   * @param fileExtension the file extension of the attachment
   *
   * @returns a string containing the file path
   */
  getMessageAttachmentFilePathAndName = (params) => {
    const { conversationId, attachmentId, fileExtension } = params;
    const attachmentFilePath = this.getMessageAttachmentFilePath(
      conversationId
    );
    const attachmentFileName = this.getAttachmentFileName({
      attachmentId,
      fileExtension,
    });

    const attachmentFilePathAndName = path.join(
      attachmentFilePath,
      attachmentFileName
    );

    return attachmentFilePathAndName;
  };

  /**
   * Find the file path for a message attachment, along with the file name and extension
   *
   * @param conversationId the id of the conversation
   * @param attachmentId the id of the attachment
   *
   * @returns return nulls if the file name and path is not found
   */
  findMessageAttachmentFilePathAndName = (params) => {
    const { conversationId, attachmentId } = params;
    const attachmentFilePath = this.getMessageAttachmentFilePath(
      conversationId
    );
    try {
      if (appConfig.fileStorage?.exists(attachmentFilePath)) {
        const fileList = appConfig.fileStorage?.getList(attachmentFilePath);

        const fileName = fileList.filter((file) => file.includes(attachmentId));

        if (validateArray(fileName)) {
          const foundFile = fileName[0];
          const filePathAndName = path.join(attachmentFilePath, foundFile);

          return filePathAndName;
        }
      }
      return "";
    } catch (error) {
      return "";
    }
  };

  /**
   * Get the file name for a TextMessageAttachment
   *
   * @param attachmentId the unique id of the attachment
   * @param fileExtension the file extension of the attachment
   *
   * @returns a string containing the file name
   */
  getAttachmentFileName = (params) => {
    const { attachmentId, fileExtension } = params;
    const fileName = `${attachmentId}.${fileExtension}`;

    return fileName;
  };

  // #endregion TextMessageAttachments

  /**
   * Clear the temp folder files and remove the temp folder itself
   */
  clearTempFoler = () => {
    const tempPath = path.join(this.userDataPath, localFilePaths.TEMP);

    appConfig.fileStorage?.remove(tempPath);
    appConfig.fileStorage?.createPath(tempPath);
  };

  /**
   * Return the full path to a file (works for electron an web)
   *
   * @param filePath the file pahth
   *
   * @returns a string containing the full file path
   */
  getFullFilePath = (filePath: string) => {
    if (appConfig.electron) {
      return path.join(appConfig.resourcesPath, filePath);
    } else {
      const url = `${window.location.protocol}//${window.location.hostname}:${window.location.port}/resources/${filePath}`;
      return url;
    }
  };
}
